import React from 'react';
import { Scale, LogOut, Briefcase, FileText, MessageSquare, LayoutDashboard, Receipt } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { useAuth } from '../../contexts/AuthContext';
import { useRoute } from '../../lib/router';

const NAV = [
  { label: 'Overview',   path: '/portal',           icon: LayoutDashboard },
  { label: 'Case Files', path: '/portal/matters',   icon: Briefcase       },
  { label: 'Invoices',   path: '/portal/invoices',  icon: FileText        },
  { label: 'Receipts',   path: '/portal/receipts',  icon: Receipt         },
  { label: 'Messages',   path: '/portal/messages',  icon: MessageSquare   },
];

export default function PortalLayout({ children }) {
  const { contact, firm } = usePortal();
  const { logout }        = useAuth();
  const [{ pathname }, navigate] = useRoute();

  const fullName = [contact?.first_name, contact?.last_name].filter(Boolean).join(' ') || contact?.email || 'Client';

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-slate-950">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between gap-4">
          {/* Brand */}
          <div className="flex items-center gap-2.5 min-w-0">
            {firm?.logo_url ? (
              <img
                src={firm.logo_url}
                alt={firm?.name || 'Firm logo'}
                className="h-8 w-auto max-w-[120px] object-contain flex-shrink-0 bg-white rounded p-0.5 border border-slate-200 dark:border-slate-700"
              />
            ) : (
              <div className="w-7 h-7 rounded-lg bg-indigo-600 flex items-center justify-center flex-shrink-0">
                <Scale size={14} className="text-white" />
              </div>
            )}
            <div className="leading-tight min-w-0">
              <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">{firm?.name || 'Client Portal'}</p>
              <p className="text-[11px] text-slate-400">Client Portal</p>
            </div>
          </div>

          {/* Desktop nav */}
          <nav className="hidden sm:flex items-center gap-1">
            {NAV.map(({ label, path, icon: Icon }) => {
              const active = path === '/portal' ? pathname === '/portal' : pathname.startsWith(path);
              return (
                <button
                  key={path}
                  onClick={() => navigate(path)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition
                    ${active
                      ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                >
                  <Icon size={14} />
                  {label}
                </button>
              );
            })}
          </nav>

          {/* User + logout */}
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-slate-600 dark:text-slate-400">{fullName}</span>
            <button
              onClick={logout}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
            >
              <LogOut size={14} />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        <div className="sm:hidden flex border-t border-slate-100 dark:border-slate-800">
          {NAV.map(({ label, path, icon: Icon }) => {
            const active = path === '/portal' ? pathname === '/portal' : pathname.startsWith(path);
            return (
              <button
                key={path}
                onClick={() => navigate(path)}
                className={`flex-1 flex flex-col items-center gap-0.5 py-2 text-[11px] font-medium transition
                  ${active
                    ? 'text-indigo-700 dark:text-indigo-300'
                    : 'text-slate-500 dark:text-slate-400'
                  }`}
              >
                <Icon size={16} />
                {label}
              </button>
            );
          })}
        </div>
      </header>

      {/* Page content */}
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 py-8">
        {children}
      </main>

      <footer className="text-center py-4 text-xs text-slate-400">
        Powered by {firm?.name || 'DocketFlow'} · Secure client portal · Phoenix Tech
      </footer>
    </div>
  );
}
